import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from 'moment';

export type CancelLimitArgs = {
  id?: number;
  cancelFeePercent: number;
  cancelFeeAmount: number;
  cancelFeeMode?: 'fixed' | 'percent';
  limitDatetime: string;
  limitDatetimeMode?: 'daysBefore' | 'calendar' | 'days_before';
  limitDatetimeTimezone?: string;
  utcOffset?: number; // Rails から秒単位で受け取る
};

const limitDatetimeModeMap: Record<string, 'daysBefore' | 'calendar'> = {
  days_before: 'daysBefore',
  daysBefore: 'daysBefore',
  calendar: 'calendar'
};

const limitDatetimeModeResponse = {
  daysBefore: 'days_before',
  calendar: 'calendar'
};

// UTCオフセット（分）からタイムゾーンを取得するヘルパー関数
function findTimezonesByCurrentOffset(targetOffset: number): string[] {
  // 基準となる日時（現在時刻）
  const now = moment();

  // moment-timezoneが知っているすべてのタイムゾーン名のリスト
  const allTimezoneNames = moment.tz.names();

  // 現在時刻において、オフセットが一致するものをフィルタリング
  let matchingTimezones = allTimezoneNames.filter(tzName => {
    // 現在時刻を各タイムゾーンに適用し、その時点でのオフセットを計算
    const currentOffsetInTimezone = moment.tz(now, tzName).utcOffset();
    return currentOffsetInTimezone === targetOffset;
  });

  // Asia/Tokyoがあればそれを優先
  if (matchingTimezones.includes('Asia/Tokyo')) {
    matchingTimezones = ['Asia/Tokyo'].concat(matchingTimezones.filter(tz => tz !== 'Asia/Tokyo'));
  }

  return matchingTimezones;
}

const getTimezoneFromUtcOffset = (utcOffsetSeconds: number): string => {
  // Railsからは秒単位で受け取るため、分単位に変換
  const utcOffsetMinutes = utcOffsetSeconds / 60;
  const matchingTimezones = findTimezonesByCurrentOffset(utcOffsetMinutes);

  // マッチするタイムゾーンがある場合は最初のものを返す、なければUTCを返す
  return matchingTimezones.length > 0 ? matchingTimezones[0] : 'UTC';
};

export default class CancelLimit {
  readonly id?: number;

  @observable cancelFeePercent: number;
  @observable cancelFeeAmount: number;
  @observable cancelFeeMode: 'fixed' | 'percent';
  @observable limitDatetime: Moment;
  @observable limitDatetimeMode: 'daysBefore' | 'calendar';
  @observable limitDatetimeTimezone: string;
  @observable utcOffset?: number;

  constructor(args: CancelLimitArgs) {
    this.id = args.id;
    this.cancelFeePercent = args.cancelFeePercent ?? 0;
    this.cancelFeeAmount = args.cancelFeeAmount ?? 0;
    this.limitDatetimeTimezone = args.limitDatetimeTimezone ?? 'Asia/Tokyo';
    this.utcOffset = args.utcOffset;

    // argsのlimitDatetimeはUTCで受け取り、utcOffsetが指定されている場合は
    // そのオフセットに対応するタイムゾーンを探して使用、そうでなければ指定されたタイムゾーンで変換
    if (this.utcOffset !== undefined) {
      const timezone = getTimezoneFromUtcOffset(this.utcOffset);
      this.limitDatetime = moment.utc(args.limitDatetime).tz(timezone);
      this.limitDatetimeTimezone = timezone; // タイムゾーンも更新
    } else {
      this.limitDatetime = moment.utc(args.limitDatetime).tz(this.limitDatetimeTimezone);
    }

    if (args.cancelFeeMode) {
      this.cancelFeeMode = args.cancelFeeMode;
    } else {
      this.cancelFeeMode = this.cancelFeeAmount > 0 ? 'fixed' : 'percent';
    }

    if (args.limitDatetimeMode) {
      this.limitDatetimeMode = limitDatetimeModeMap[args.limitDatetimeMode];
    } else {
      // If time is 23:59, use 'calendar', else 'daysBefore'
      const hour = this.limitDatetime.hour();
      const minute = this.limitDatetime.minute();
      this.limitDatetimeMode = hour === 23 && minute === 59 ? 'daysBefore' : 'calendar';
    }
  }

  get timeZoneLabel() {
    if (this.limitDatetimeTimezone === 'Asia/Tokyo') {
      return null;
    }

    if (this.utcOffset) {
      const gmtOffset = this.utcOffset / 3600;
      const gmtSign = gmtOffset >= 0 ? '+' : '-';
      return `GMT${gmtSign}${Math.abs(gmtOffset).toString().padStart(2, '0')}:00`;
    }

    return this.limitDatetimeTimezone;
  }

  setCancelFeePercent = (value: number) => {
    this.cancelFeePercent = value;
    this.cancelFeeAmount = 0;
    this.cancelFeeMode = 'percent';
    app.render();
  };

  setCancelFeeAmount = (value: number) => {
    this.cancelFeeAmount = value;
    this.cancelFeePercent = 0;
    this.cancelFeeMode = 'fixed';
    app.render();
  };

  setCancelFeeMode = (mode: 'fixed' | 'percent') => {
    this.cancelFeeMode = mode;
    if (mode === 'fixed') {
      this.cancelFeePercent = 0;
    } else {
      this.cancelFeeAmount = 0;
    }
    app.render();
  };

  setLimitDatetimeMode = (mode: 'daysBefore' | 'calendar') => {
    this.limitDatetimeMode = mode;
    app.render();
  };

  setLimitDatetime = (date: Moment) => {
    this.limitDatetime = date;
    app.render();
  };

  setDaysBefore = (value: number, checkinDate: Moment) => {
    if (value < 0) return;
    // 指定した日の終日を対象に含めるため、23:59をデフォルトにする
    const date = checkinDate.clone().tz(this.limitDatetimeTimezone).endOf('day');
    this.limitDatetime = date.subtract(value, 'days');
    app.render();
  };

  daysBefore = (checkinDate: Moment) => {
    return checkinDate.clone().startOf('day').diff(this.limitDatetime.clone().startOf('day'), 'days');
  };

  updateParams = () => {
    return {
      limit_datetime: this.limitDatetime.clone().utc().format('YYYY-MM-DD HH:mm:ss ZZ'),
      limit_datetime_mode: limitDatetimeModeResponse[this.limitDatetimeMode],
      limit_datetime_timezone: this.limitDatetimeTimezone,
      cancel_fee_percent: this.cancelFeePercent,
      cancel_fee_amount: this.cancelFeeAmount
    };
  };

  validationErrors = (): string[] => {
    const errors: string[] = [];
    if (this.cancelFeePercent < 0 || this.cancelFeePercent > 100) {
      errors.push('キャンセルポリシーのキャンセル料率は0〜100の間で指定してください。');
    }
    return errors;
  };
}
